import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import pickBy from 'lodash/pickBy';

import { createFrozenMap } from '@/vuex/utils';

import { fetchCoreEventGroups, patchCoreEvents, fetchCoreEventsEmptyState } from './core-events.utils';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

const filterCoreEvents = (countable) => countable.isCoreEvent;

let loadAllPromise;
let loadEmptyStatePromise;

export default {
    namespaced: true,
    state: {
        error: {},
        fetching: false,
        map: {},
        coreEventGroups: {},
        updateCoreEventErrors: {
            error: null,
            appsWithConflicts: [],
        },
        isEveryCoreEventGroupEmpty: null,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setCoreEventGroups (state, { coreEventGroups }) {
            state.coreEventGroups = coreEventGroups;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setUpdateCoreEventErrors (state, { response = {} } = {}) {
            const { code, group: { id, appId } = {} } = response;

            let errorName = 'error';
            if (code === 409) {
                errorName = 'conflict';
                state.updateCoreEventErrors.appsWithConflicts.push(appId);
            }
            if (id) {
                state.updateCoreEventErrors[id] = errorName;
            }
            state.updateCoreEventErrors.error = errorName;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        resetUpdateCoreEventErrors (state, { coreEventGroupId = null } = {}) {
            if (coreEventGroupId) {
                state.updateCoreEventErrors[coreEventGroupId] = null;

                return;
            }
            state.updateCoreEventErrors = {
                error: null,
                appsWithConflicts: [],
            };
        },
        setEmptyState (state, { emptyState }) {
            state.isEveryCoreEventGroupEmpty = emptyState;
        },
    },
    actions: {
        async loadAll ({ commit, state, dispatch, rootGetters }, { reFetch = false } = {}) {
            const hasAnalyticsProductLine = rootGetters['productLines/hasProductLinePurchased'](
                PRODUCT_LINES.ANALYTICS,
            );
            if (!hasAnalyticsProductLine) {
                return;
            }

            if (!isEmpty(state.map) && !reFetch && isEmpty(state.error)) return;

            if (reFetch || !loadAllPromise) {
                loadAllPromise = (async () => {
                    commit('setError', { error: {} });
                    commit('setFetching', { fetching: true });

                    // Because we allow a user to delete a countable without our BE updating its group (core event group in this case)
                    // the countables source needs to be the source of truth for core events

                    try {
                        await Promise.all([
                            dispatch('loadCoreEventsEmptyState', { reFetch }),
                            dispatch('features/loadAll', { reFetch }, { root: true }),
                            dispatch('trackTypes/loadAll', { reFetch }, { root: true }),
                            dispatch('pages/loadAll', { reFetch }, { root: true }),
                        ]);
                    } catch (error) {
                        commit('setFetching', { fetching: false });
                        commit('setError', { error });
                        throw error;
                    }

                    const features = rootGetters['features/list'];
                    const trackTypes = rootGetters['trackTypes/list'];
                    const pages = rootGetters['pages/list'];

                    let newCoreEventsList = [];

                    if (features.length) {
                        newCoreEventsList = newCoreEventsList.concat(features.filter(filterCoreEvents));
                    }

                    if (trackTypes.length) {
                        newCoreEventsList = newCoreEventsList.concat(trackTypes.filter(filterCoreEvents));
                    }

                    if (pages.length) {
                        newCoreEventsList = newCoreEventsList.concat(pages.filter(filterCoreEvents));
                    }

                    commit('setFetching', { fetching: false });
                    commit('setMap', {
                        map: createFrozenMap(newCoreEventsList),
                    });
                })();
            }

            try {
                await loadAllPromise;
            } finally {
                loadAllPromise = null;
            }
        },
        async loadCoreEventsEmptyState ({ commit, state }, { reFetch = false } = {}) {
            if (state.isEveryCoreEventGroupEmpty !== null && !reFetch && isEmpty(state.error)) return;

            if (reFetch || !loadEmptyStatePromise) {
                loadEmptyStatePromise = (async () => {
                    const emptyState = await fetchCoreEventsEmptyState();
                    commit('setEmptyState', { emptyState: emptyState.data.isempty });
                })();
            }

            try {
                await loadEmptyStatePromise;
            } finally {
                loadEmptyStatePromise = null;
            }
        },
        async loadCoreEventGroups ({ commit, state }, { reFetch = false } = {}) {
            if (!isEmpty(state.coreEventGroups) && !reFetch) return;
            commit('setFetching', { fetching: true });
            let coreEventGroups;

            try {
                coreEventGroups = await fetchCoreEventGroups();
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }

            commit('setFetching', { fetching: false });

            commit('setCoreEventGroups', {
                coreEventGroups: createFrozenMap(coreEventGroups.data),
            });
        },
        async patch ({ commit, dispatch }, payload) {
            commit('setFetching', { fetching: true });

            try {
                const { data } = await patchCoreEvents(payload);
                data.forEach((response) => {
                    if (response.code !== 200) {
                        commit('setUpdateCoreEventErrors', { response });
                    }
                });
            } catch (error) {
                commit('setUpdateCoreEventErrors');
            }
            await Promise.all([
                dispatch('loadAll', { reFetch: true }),
                dispatch('loadCoreEventGroups', { reFetch: true }),
            ]);
            commit('setFetching', { fetching: false });
        },
    },
    getters: {
        hasCoreEvents: (state, _getters, _rootState, rootGetters) => {
            return (
                rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.ANALYTICS) &&
                state.isEveryCoreEventGroupEmpty === false
            );
        },
        list: (state) => values(state.map),
        analyticsCoreEventsListWithStars: (state, getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];
            if (state.fetching) return null;

            return getters.list.map((coreEvent) =>
                Object.freeze({
                    header: coreEvent.header || 'core',
                    appDisplayName:
                        coreEvent.appDisplayName || rootGetters['apps/getAppById'](coreEvent.appId)?.displayName,
                    id: coreEvent.id,
                    name: coreEvent.name,
                    group: coreEvent.group,
                    appId: coreEvent.appId,
                    appIcon: rootGetters['apps/getIconByAppId'](coreEvent.appId),
                    kind: coreEvent.kind,
                    dirty: coreEvent.dirty,
                    isCoreEvent: coreEvent.isCoreEvent,
                    isStarred: !!isStarred(coreEvent.id),
                    icon: coreEvent.icon,
                }),
            );
        },
        coreEventById: (state) => (id) => state.map[id],
        coreEventListByAppId: (state) => (appId) => {
            if (state.fetching) return {};

            return values(pickBy(state.map, (countable) => countable.appId === appId));
        },
        coreEventListByKind: (state) => (kind) => {
            return values(pickBy(state.map, (countable) => countable.kind === kind));
        },
        coreEventsGroupByAppId: (state) => (appId) => {
            if (state.fetching) return {};
            let coreEventGroupId;

            return pickBy(state.coreEventGroups, (coreEventGroup) => {
                if (coreEventGroup.appId === appId) {
                    coreEventGroupId = coreEventGroup.id;

                    return true;
                }
            })[coreEventGroupId];
        },
        coreEventListByKindAndApp: (state) => (kind, appId) => {
            return values(pickBy(state.map, (countable) => countable.kind === kind && countable.appId === appId));
        },
        excludeCoreEventsList: (state) => (list) => {
            return list.filter((countable) => !state.map[countable.id]);
        },
    },
};
